import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { useSessionContextSources } from '@/hooks/useSessionContextSources';
import { useQuerySessionById } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

/**
 * Minimal shape of the recording timer slice this hook reads. Typed locally so
 * the hook stays off the frozen `@/store` import (the Rematch containment
 * ratchet); `hasStartedTimer` is a `@rematch/select` selector (`seconds > 0`),
 * not a state field, so we recompute it from `seconds` here.
 */
type TimerSliceState = {
  timerModel: {
    seconds: number;
    isRunning: boolean;
  };
};

/**
 * Whether the singleton recording timer is running for this session. It is
 * ignored only when another session owns the recording; a null owner counts.
 */
export const useIsRecordingTimerRunning = (sessionId: string): boolean => {
  const isTimerRunning = useSelector(
    (state: TimerSliceState) => state.timerModel.isRunning
  );
  const recordingSessionId = useAtomValue(activeSessionIdAtom);
  return (
    isTimerRunning && (recordingSessionId === null || recordingSessionId === sessionId)
  );
};

/**
 * Whether this session is a context-only generation target: it has usable
 * context and no recording, so note generation must tell the backend not to
 * look for a transcript that never existed (via
 * `generate_output_without_recording`).
 *
 * Two deliberate halves, both required:
 *  - No recording (`!isRunning && !hasStartedTimer && duration === 0`), not the
 *    Create button's create-from-context UI derivation — the picker can be
 *    opened while a recording runs, so it must not treat a live consult as
 *    context-only.
 *  - Usable, settled context (`hasUsableContext && !hasPendingContextFiles`),
 *    matching every other producer of `isCreatingForContextOnlySessionAtom`
 *    (the Create button is disabled without context or while uploads are in
 *    flight). Without it a brand-new empty session would be marked context-only
 *    and generate an empty note, and the persisted flag would then let a later
 *    recording generate without its transcript.
 */
export const useIsSessionWithoutRecording = (sessionId?: string): boolean => {
  const hasStartedTimer = useSelector(
    (state: TimerSliceState) => state.timerModel.seconds > 0
  );
  const isRunning = useSelector((state: TimerSliceState) => state.timerModel.isRunning);
  const { data: duration } = useQuerySessionById<number | undefined>({
    sessionId: sessionId ?? '',
    select: (data) => data?.duration,
  });
  const { hasUsableContext, hasPendingContextFiles } = useSessionContextSources(
    sessionId ?? ''
  );

  return (
    !isRunning &&
    !hasStartedTimer &&
    duration === 0 &&
    hasUsableContext &&
    !hasPendingContextFiles
  );
};
